import {
  FC,
  ReactNode,
  useRef,
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  RefObject,
} from "react";
import { ArrowBack } from "src/icons/ArrowBack";
import { ArrowNext } from "src/icons/ArrowNext";
import {
  Container,
  ArrowContainer,
  LeftArrowButton,
  RightArrowButton,
  ScrollArea,
} from "./ScrollableRow.styled";

const ScrollableRowContext = createContext<RefObject<HTMLDivElement> | null>(
  null
);

export const useScrollableRowRef = () => useContext(ScrollableRowContext);

interface ScrollableRowProps {
  children: ReactNode;
  gap?: string;
  className?: string;
}

// A custom scrollable row component with slider arrows
const ScrollableRow: FC<ScrollableRowProps> = ({
  children,
  gap,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const hasOverflow = scrollWidth > clientWidth;
    const rightThreshold = 48;

    setCanScrollLeft(hasOverflow && scrollLeft > 2);
    setCanScrollRight(
      hasOverflow && scrollLeft < scrollWidth - clientWidth - rightThreshold
    );
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    const container = containerRef.current;
    if (!el) return;

    updateScrollState();
    const timeout = setTimeout(updateScrollState, 100);

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    if (container) observer.observe(container);

    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState, children]);

  const scroll = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const scrollStep = Math.floor(clientWidth * 0.5);
    const maxScroll = scrollWidth - clientWidth;

    let targetScroll: number;
    if (direction === 1) {
      const remaining = maxScroll - scrollLeft;
      targetScroll =
        remaining <= scrollStep ? maxScroll : scrollLeft + scrollStep;
    } else {
      targetScroll = scrollLeft <= scrollStep ? 0 : scrollLeft - scrollStep;
    }

    el.scrollTo({
      left: targetScroll,
      behavior: "smooth",
    });
  };

  return (
    <ScrollableRowContext.Provider value={containerRef}>
      <Container ref={containerRef} className={className}>
        <ArrowContainer $visible={canScrollLeft}>
          <LeftArrowButton
            onClick={() => scroll(-1)}
            aria-label="Scroll left"
            type="button"
          >
            <ArrowBack />
          </LeftArrowButton>
        </ArrowContainer>

        <ScrollArea ref={scrollRef} $gap={gap}>
          {children}
        </ScrollArea>

        <ArrowContainer $visible={canScrollRight}>
          <RightArrowButton
            onClick={() => scroll(1)}
            aria-label="Scroll right"
            type="button"
          >
            <ArrowNext />
          </RightArrowButton>
        </ArrowContainer>
      </Container>
    </ScrollableRowContext.Provider>
  );
};

export { ScrollableRow };
